import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DispatchConfirmInfo } from '../models/DispatchConfirmInfo';
import { TruckerConfirmRequest } from '../models/TruckerConfirmRequest';
import { ErrorState, SendDispatchFormState } from '../models/FormState';
import { TruckerConfirmationService } from '../services/trucker-confirmation.service';
import { ScheduleDataService } from '../services/schedule-data.service';
import { FormStatus } from '../models/Enums';
import { TruckerConfirmResults } from '../models/TruckerConfirmResults';
import { Subject, debounceTime } from 'rxjs';
import { TruckerContact } from '../models/TruckerContact';
import { MOBILE_BREAKPOINT } from '../constants/app.constants';
import { ContactPhoneSelection } from '../models/ContactPhoneSelection';

@Component({
    selector: 'confirm-dispatch',
    templateUrl: 'confirm-dispatch.component.html',
    styles: [],
    standalone: false
})
export class ConfirmDispatchComponent implements OnInit, OnChanges {

    constructor(private _truckerConfirmService: TruckerConfirmationService) {

    }

    @ViewChild('toggleScoularContactDropdown') toggleScoularContactDropdownRef: ElementRef;
    @ViewChild('toggleTruckerContactDropdown') toggleTruckerContactDropdownRef: ElementRef;
    @ViewChild('closeButton') closeButton;
    @Input() dispatchId: number = 0;
    @Input() refresh: boolean = false;
    @Input() confirmDispatchFormId: string = 'confirmDispatchModal';
    @Input() confirmTruckersFormId: string = 'confirmTruckersModal';
    @Output() sendComplete: EventEmitter<number[]> = new EventEmitter<number[]>();


    loadsStatusModalId: string = this.confirmDispatchFormId + '-LoadsStatusModal';
    loadsStatusDispatchId: number = 0;
    loadsStatusRefresh: boolean = false;

    errorMessage: string = '';
    readonly FormStatus = FormStatus;
    formStatus: FormStatus = FormStatus.Normal;
    formState: SendDispatchFormState = new SendDispatchFormState();

    dispatchConfirmInfo: DispatchConfirmInfo = new DispatchConfirmInfo();
    confirmRequest: TruckerConfirmRequest = new TruckerConfirmRequest();
    confirmResults: TruckerConfirmResults = new TruckerConfirmResults();
    emailSent: boolean = false;
    sendButtonEnabled = true;
    isMobile: boolean;
    textSent: boolean = null;
    scoularContactSearch: string = "";
    truckerContactSearch: string = "";
    showTruckerContact: boolean = false;
    showScoularContact: boolean = false;

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        const isTruckerContactDropdown = this.toggleTruckerContactDropdownRef?.nativeElement?.contains(target);
        const isScoularContactDropdown = this.toggleScoularContactDropdownRef?.nativeElement?.contains(target);

        if (!isTruckerContactDropdown && this.showTruckerContact) {
            this.showTruckerContact = false;
            this.truckerContactSearch = "";
        }

        if (!isScoularContactDropdown && this.showScoularContact) {
            this.showScoularContact = false;
            this.scoularContactSearch = "";
        }
    }

    toggleTruckerSearch(): void {
        if (this.showTruckerContact) {
            this.showTruckerContact = false;
        } else {
            this.showTruckerContact = true;
            this.showScoularContact = false;
        }
        this.truckerContactSearch = "";
    }

    toggleContactSearch() {
        if (this.showScoularContact) {
            this.showScoularContact = false;
        } else {
            this.showScoularContact = true;
            this.showTruckerContact = false;
        }
        this.scoularContactSearch = "";
    }

    truckerContactChange() {
        this.validateTruckerContact();
        const hasValidPhones = this.hasValidTextPhones();
        this.confirmRequest.sendText = hasValidPhones;
    }

    scoularContactChange() {
        this.validateScoularContact();
    }

    sendEmailChange() {
        this.validateTruckerContact();
    }

    sendTextChange() {
        this.validateTruckerContact();
    }

    validateTruckerContact() {
        this.formState.truckerContact = new ErrorState();
        if (!this.formState.submitted || (!this.confirmRequest.sendEmail && !this.confirmRequest.sendText)) {
            return;
        }

        if (this.confirmRequest.truckerContactIds.length === 0) {
            this.formState.truckerContact.invalid = true;
            this.formState.truckerContact.errorMessage = 'Trucker contact is required';
            return;
        }

        //email validation
        if (this.confirmRequest.sendEmail) {
            const hasValidEmail = this.dispatchConfirmInfo.truckerContactList
                .filter(c => this.confirmRequest.truckerContactIds.includes(c.contactId))
                .some(c => c.email && c.email.trim().length > 0);

            if (!hasValidEmail) {
                this.formState.truckerContact.invalid = true;
                this.formState.truckerContact.errorMessage = 'At least one selected contact must have a valid email address to send email confirmation';
            }
        }

        //text validation
        if (this.confirmRequest.sendText) {
            let hasAtLeastOneValidPhone = false;

            for (const contactId of this.confirmRequest.truckerContactIds) {
                const contact = this.dispatchConfirmInfo.truckerContactList.find(x => x.contactId === contactId);
                if (!contact) continue;

                const phoneSelection = this.confirmRequest.selectedPhones.find(p => p.contactId === contactId);
                if (phoneSelection && (phoneSelection.phone1Selected || phoneSelection.phone2Selected)) {
                    hasAtLeastOneValidPhone = true;
                }
            }

            if (!hasAtLeastOneValidPhone) {
                this.formState.truckerContact.invalid = true;
                const existingError = this.formState.truckerContact.errorMessage;
                const textError = 'At least one contact must have a selected phone number when sending text.';
                this.formState.truckerContact.errorMessage = existingError ? `${existingError} and ${textError.toLowerCase()}` : textError;
            }
        }
    }

    validateScoularContact() {
        this.formState.scoularContact = new ErrorState();
        if (this.formState.submitted) {
            if (this.confirmRequest.scoularUserIds?.length == 0) {
                this.formState.scoularContact.invalid = true;
                this.formState.scoularContact.errorMessage = 'Scoular contact is required';
            }
        }
    }

    validateSendOptions() {
        this.formState.sendOptions = new ErrorState();
        if (this.formState.submitted) {
            if (!this.confirmRequest.sendEmail && !this.confirmRequest.sendRoger && !this.confirmRequest.sendText && !this.confirmRequest.sendCompuweigh) {
                this.formState.sendOptions.invalid = true;
                this.formState.sendOptions.errorMessage = 'At least 1 send option must be selected';
            }
        }
    }

    validateForm() {
        this.validateTruckerContact();
        this.validateScoularContact();
        this.validateSendOptions();
    }

    private sendClick = new Subject<void>();

    onSendClick() {
        this.sendClick.next();
    }

    isSendingEmail: boolean = false;
    isSendingText: boolean = false;

    sendDispatch(): void {
        this.formState.submitted = true;
        this.validateForm();

        if (this.formState.isValid()) {
            if (this.confirmRequest.sendEmail || this.confirmRequest.sendText || this.confirmRequest.sendRoger || this.confirmRequest.sendCompuweigh) {
                this.formStatus = FormStatus.Saving;
            }

            //email
            this.emailSent = false;
            this.textSent = null;

            let pendingOperations = 0;
            let completedOperations = 0;
            const hasRoger = this.confirmRequest.sendRoger;

            if (this.confirmRequest.sendEmail) pendingOperations++;
            if (this.confirmRequest.sendText) pendingOperations++;
            if (this.confirmRequest.sendRoger) pendingOperations++;
            if (this.confirmRequest.sendCompuweigh) pendingOperations++;

            const checkAndClose = () => {
                completedOperations++;
                if (completedOperations >= pendingOperations) {
                    this.formStatus = FormStatus.Normal;

                    if (!hasRoger) {
                        setTimeout(() => {
                            this.closeButton.nativeElement.click();
                        }, 3000);
                    }
                }
            };

            if (this.confirmRequest.sendEmail) {
                this._truckerConfirmService.sendEmailConfirmation(this.confirmRequest).subscribe({
                    error: err => {
                        this.errorMessage = err;
                        this.formStatus = FormStatus.Normal;
                    },
                    next: data => {
                        this.emailSent = true;
                        this.sendComplete.emit(data.scheduleIds);
                        checkAndClose();
                    },
                });
            }

            //roger
            if (this.confirmRequest.sendRoger) {
                this._truckerConfirmService.sendRogerConfirmation(this.confirmRequest).subscribe({
                    next: data => {
                        this.confirmResults.rogerSendComplete = data.rogerSendComplete;
                        this.confirmResults.rogerSuccess = data.rogerSuccess;
                        this.confirmResults.rogerResult = data.rogerResult;
                        this.sendComplete.emit(data.scheduleIds);
                        this.sendButtonEnabled = true;
                        checkAndClose();
                    },
                    error: err => {
                        this.errorMessage = err;
                        this.formStatus = FormStatus.Normal;
                        this.sendButtonEnabled = false;
                        completedOperations++;
                    }
                });
            }

            //text
            if (this.confirmRequest.sendText) {
                this._truckerConfirmService.sendTextConfirmation(this.confirmRequest).subscribe({
                    next: data => {
                        this.textSent = data.textResult;
                        if (data.textResult) {
                            this.sendComplete.emit(data.scheduleIds);
                        }
                        checkAndClose();
                    },
                    error: err => {
                        this.errorMessage = err;
                        this.formStatus = FormStatus.Normal;
                        this.formState.sendOptions = new ErrorState();
                        this.formState.sendOptions.invalid = true;
                        this.formState.sendOptions.errorMessage = 'Error occurred while sending text message.';
                        completedOperations++;
                    }
                });
            }

            //compuweigh
            if (this.confirmRequest.sendCompuweigh) {
                this._truckerConfirmService.sendCompuweighConfirmation(this.confirmRequest).subscribe({
                    next: data => {
                        this.confirmResults.compuweighSendComplete = data.compuweighSendComplete;
                        this.confirmResults.compuweighSuccess = data.compuweighSuccess;
                        this.confirmResults.compuweighResult = data.compuweighResult;
                        this.sendComplete.emit(data.scheduleIds);
                        checkAndClose();
                    },
                    error: err => {
                        this.errorMessage = err;
                        this.confirmResults.compuweighSendComplete = true;
                        this.confirmResults.compuweighSuccess = false;
                        this.confirmResults.compuweighResult = 'CompuWeigh send failed';
                        completedOperations++;
                    }
                });
            }
        }
    }

    showLoadsStatus() {
        this.loadsStatusDispatchId = this.confirmRequest.dispatchId;
        this.loadsStatusRefresh = !this.loadsStatusRefresh;
        return false;
    }

    resetComponent() {
        this.dispatchConfirmInfo = new DispatchConfirmInfo();
        this.formState = new SendDispatchFormState();
        this.formStatus = FormStatus.Normal;
        this.confirmRequest = new TruckerConfirmRequest();
        this.confirmResults = new TruckerConfirmResults();
        this.emailSent = false;
        this.textSent = null;
        this.sendButtonEnabled = true;
        this.showTruckerContact = false;
        this.showScoularContact = false;
        this.truckerContactSearch = "";
        this.scoularContactSearch = "";
    }

    getDispatchConfirmInfo(): void {
        if (this.dispatchId > 0) {
            this._truckerConfirmService.getDispatchConfirmInfo(this.dispatchId).subscribe({
                next: data => {
                    this.dispatchConfirmInfo = data;
                    this.confirmRequest.dispatchId = this.dispatchId;
                    this.confirmRequest.truckerId = this.dispatchConfirmInfo.dispatch.truckerId;

                    const primary = this.dispatchConfirmInfo.truckerContactList.find(c => c.isPrimary && c.contactId > 0);
                    if (primary) {
                        this.confirmRequest.truckerContactIds.push(primary.contactId);
                        this.initializePhoneSelections(primary.contactId);
                    } else {
                        this.confirmRequest.truckerContactIds.push(0);
                        this.initializePhoneSelections(0);
                    }

                    this.confirmRequest.scoularUserIds.push(this.dispatchConfirmInfo.currentUserId);
                    this.confirmRequest.sendEmail = true;
                    this.confirmRequest.sendRoger = this.dispatchConfirmInfo.truckerRogerEnabled;
                    this.confirmRequest.sendCompuweigh = this.dispatchConfirmInfo.dispatchCompuweighEligible;
                    this.confirmRequest.loadBreakoutOverride = this.dispatchConfirmInfo.loadBreakoutOverride;
                    this.updateTextToggle();
                    this.sendButtonEnabled = !data.dispatch.rogerInProcess;
                },
                error: err => this.errorMessage = err
            });
        }
    }

    updateTextToggle() {
        const hasValidPhones = this.hasValidTextPhones();
        this.confirmRequest.sendText = hasValidPhones;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['confirmDispatchFormId']) {
            this.loadsStatusModalId = this.confirmDispatchFormId + '-LoadsStatusModal';
        }
        if (changes['dispatchId'] || changes['refresh']) {
            this.resetComponent();
            this.getDispatchConfirmInfo();
        }
    }

    ngOnInit(): void {
        if (window.innerWidth <= MOBILE_BREAKPOINT) {
            this.isMobile = true;
        }

        //prevent double click from calling save twice
        this.sendClick.pipe(
            debounceTime(500)
        ).subscribe(() => this.sendDispatch());
    }

    filteredTruckerContacts() {
        const search = this.truckerContactSearch?.toLowerCase();
        return this.dispatchConfirmInfo.truckerContactList.filter(item =>
            item.contactName.toLowerCase().includes(search)
        );
    }

    filteredScoularContacts() {
        const search = this.scoularContactSearch?.toLowerCase();
        return this.dispatchConfirmInfo.scoularContactList.filter(item =>
            item.text.toLowerCase().includes(search)
        );
    }

    toggleTruckerContact(contactId: number) {
        const index = this.confirmRequest.truckerContactIds.indexOf(contactId);
        if (index > -1) {
            this.confirmRequest.truckerContactIds.splice(index, 1);
            this.removePhoneSelections(contactId);
        } else {
            this.confirmRequest.truckerContactIds.push(contactId);
            this.initializePhoneSelections(contactId);
        }
        this.truckerContactChange();
    }

    toggleScoularContact(contactId: number) {
        const index = this.confirmRequest.scoularUserIds.indexOf(contactId);
        if (index > -1) {
            this.confirmRequest.scoularUserIds.splice(index, 1);
        } else {
            this.confirmRequest.scoularUserIds.push(contactId);
        }
        this.scoularContactChange();
    }

    togglePhone(contactId: number, phoneNumber: 1 | 2) {
        let phoneSelection = this.confirmRequest.selectedPhones.find(p => p.contactId === contactId);

        if (!phoneSelection) {
            phoneSelection = new ContactPhoneSelection();
            phoneSelection.contactId = contactId;
            this.confirmRequest.selectedPhones.push(phoneSelection);
        }

        const contact = this.dispatchConfirmInfo.truckerContactList.find(c => c.contactId === contactId);
        if (contact && this.isPhoneDuplicate(contact)) {
            const isSelected = phoneNumber === 1 ? phoneSelection.phone1Selected : phoneSelection.phone2Selected;
            phoneSelection.phone1Selected = !isSelected;
            phoneSelection.phone2Selected = !isSelected;
        } else {
            if (phoneNumber === 1) {
                phoneSelection.phone1Selected = !phoneSelection.phone1Selected;
            } else {
                phoneSelection.phone2Selected = !phoneSelection.phone2Selected;
            }
        }

        const hasValidPhones = this.hasValidTextPhones();
        this.confirmRequest.sendText = hasValidPhones;

        this.truckerContactChange();
    }

    hasValidTextPhones(): boolean {
        if (this.confirmRequest.truckerContactIds.length === 0) {
            return false;
        }

        for (const contactId of this.confirmRequest.truckerContactIds) {
            const contact = this.dispatchConfirmInfo.truckerContactList.find(x => x.contactId === contactId);
            if (!contact) continue;

            const phoneSelection = this.confirmRequest.selectedPhones.find(p => p.contactId === contactId);
            if (phoneSelection && (phoneSelection.phone1Selected || phoneSelection.phone2Selected)) {
                return true; 
            }
        }
        return false;
    }

    initializePhoneSelections(contactId: number) {
        const phoneSelection = new ContactPhoneSelection();
        phoneSelection.contactId = contactId;

        const contact = this.dispatchConfirmInfo.truckerContactList.find(c => c.contactId === contactId);
        if (contact) {
            if (contactId === 0) {
                phoneSelection.phone1Selected = !!(contact.isPhone1TextEnabled && contact.phone1?.trim());
                phoneSelection.phone2Selected = false;
            } else {
                if (this.isPhoneDuplicate(contact)) {
                    const shouldSelect = (contact.isPhone1Default && contact.isPhone1TextEnabled) || (contact.isPhone2Default && contact.isPhone2TextEnabled);

                    phoneSelection.phone1Selected = !!shouldSelect;
                    phoneSelection.phone2Selected = !!shouldSelect;
                } else {
                    phoneSelection.phone1Selected = !!(contact.isPhone1Default && contact.isPhone1TextEnabled);
                    phoneSelection.phone2Selected = !!(contact.isPhone2Default && contact.isPhone2TextEnabled);
                }
            }
        }

        this.confirmRequest.selectedPhones.push(phoneSelection);
    }

    removePhoneSelections(contactId: number) {
        const index = this.confirmRequest.selectedPhones.findIndex(p => p.contactId === contactId);
        if (index > -1) {
            this.confirmRequest.selectedPhones.splice(index, 1);
        }
    }

    isPhoneSelected(contactId: number, phoneNumber: 1 | 2): boolean {
        const phoneSelection = this.confirmRequest.selectedPhones.find(p => p.contactId === contactId);
        if (!phoneSelection) return false;

        return phoneNumber === 1 ? phoneSelection.phone1Selected : phoneSelection.phone2Selected;
    }

    isPhoneAvailableForText(contact: TruckerContact, phoneNumber: 1 | 2): boolean {
        if (phoneNumber === 1) {
            return contact.isPhone1TextEnabled && contact.phone1?.trim().length > 0;
        } else {
            return contact.isPhone2TextEnabled && contact.phone2?.trim().length > 0;
        }
    }

    isPhoneDuplicate(contact: TruckerContact): boolean {
        const phone1 = contact.phone1?.trim();
        const phone2 = contact.phone2?.trim();

        return !!(phone1 && phone2 && phone1 === phone2);
    }

    shouldShowPhone2(contact: TruckerContact): boolean {
        return this.isPhoneAvailableForText(contact, 2) && !this.isPhoneDuplicate(contact);
    }

    getTruckerContactDisplay(): string {
        const selectedIds = this.confirmRequest.truckerContactIds;
        const list = this.dispatchConfirmInfo.truckerContactList;

        if (selectedIds.length === 1) {
            const selected = list.find(c => c.contactId === selectedIds[0]);
            if (selected) {
                const phoneSelection = this.confirmRequest.selectedPhones.find(p => p.contactId === selected.contactId);
                let phoneDisplay = '';
                const selectedPhones: string[] = [];

                if (phoneSelection) {
                    if (this.isPhoneDuplicate(selected)) {
                        if ((phoneSelection.phone1Selected || phoneSelection.phone2Selected) && selected.phone1?.trim()) {
                            selectedPhones.push(selected.phone1.trim());
                        }
                    } else {
                        if (phoneSelection.phone1Selected && selected.phone1?.trim()) {
                            selectedPhones.push(selected.phone1.trim());
                        }
                        if (phoneSelection.phone2Selected && selected.phone2?.trim()) {
                            selectedPhones.push(selected.phone2.trim());
                        }
                    }
                }

                if (selectedPhones.length > 0) {
                    phoneDisplay = ` (${selectedPhones.join(', ')})`;
                }

                return `${selected.contactName}${selected.email ? ` (${selected.email})` : ""}${phoneDisplay}`;

            }
        }

        if (selectedIds.length > 1) {
            const uniquePhones = new Set<string>();

            selectedIds.forEach(contactId => {
                const contact = list.find(c => c.contactId === contactId);
                const phoneSelection = this.confirmRequest.selectedPhones.find(p => p.contactId === contactId);

                if (phoneSelection && contact) {
                    if (this.isPhoneDuplicate(contact)) {
                        if ((phoneSelection.phone1Selected || phoneSelection.phone2Selected) && contact.phone1?.trim()) {
                            uniquePhones.add(contact.phone1.trim());
                        }
                    } else {
                        if (phoneSelection.phone1Selected && contact.phone1?.trim()) {
                            uniquePhones.add(contact.phone1.trim());
                        }
                        if (phoneSelection.phone2Selected && contact.phone2?.trim()) {
                            uniquePhones.add(contact.phone2.trim());
                        }
                    }
                }
            });

            const phoneCount = uniquePhones.size;
            const phoneText = phoneCount === 1 ? 'phone selected' : 'phones selected';
            return `${selectedIds.length} selected contacts (${phoneCount} ${phoneText})`;
        }

        return 'Select contact(s)';
    }

    getScoularContactDisplay(): string {
        const selectedIds = this.confirmRequest.scoularUserIds;
        const list = this.dispatchConfirmInfo.scoularContactList;

        if (selectedIds.length === 1) {
            const selected = list.find(c => c.id === selectedIds[0]);
            return selected?.text;
        }

        if (selectedIds.length > 1) {
            return `${selectedIds.length} selected contacts`;
        }

        return 'Select contact(s)';
    }

}
